import { useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import Icon from '../../Components/Icon'
import { useCreateCardMutation, useGetConfigQuery, useGetLearnedQuery, useGetPlanQuery, useUpdateConfigMutation } from '../../Redux/Apis/leitnerApi'
import { tehranTodayYMD, formatTehranDate } from '../../utils/time'
import type { RootState } from '../../Redux/Store/Store'
import { useSelector } from 'react-redux'

export default function Leitner() {
  const navigate = useNavigate()
  const today = tehranTodayYMD(new Date())

  // Data
  const { data: plan } = useGetPlanQuery({ date: today })
  const { data: learned } = useGetLearnedQuery()
  const { data: config } = useGetConfigQuery()
  const [updateConfig] = useUpdateConfigMutation()

  // Create new card
  const [createCard] = useCreateCardMutation()
  const [front, setFront] = useState('')
  const [creating, setCreating] = useState(false)

  // Token for CSV download
  const token = useSelector((s: RootState) => s.auth.token)

  const countsByBox = useMemo(() => {
    const map: Record<number, number> = {}
    for (const row of (plan?.byDeck ?? [])) map[row.box] = row.count
    return map
  }, [plan])

  const boxCards = Array.from({ length: 5 }).map((_, idx) => ({
    n: idx + 1,
    title: `جعبه ${idx + 1}`,
    count: countsByBox[idx + 1] ?? 0,
  }))

  const onCreate = async () => {
    if (!front.trim() || creating) return
    setCreating(true)
    try {
      await createCard({ front, box: 1 }).unwrap()
      setFront('')
      ;(document.getElementById('modalAddClose') as HTMLButtonElement | null)?.click()
    } catch (_) { /* noop */ } finally { setCreating(false) }
  }

  const downloadCSV = async () => {
    try {
      const res = await fetch('/api/cards/export.csv?learnedOnly=1', {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'grehub_leitner.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (_) {}
  }

  const [reviewNumber, setReviewNumber] = useState<number>(30)
  useEffect(() => { if (typeof config?.reviewNumber === 'number') setReviewNumber(config.reviewNumber) }, [config])

  const saveConfig = async () => {
    const v = Math.max(1, Math.min(200, Math.floor(Number(reviewNumber) || 30)))
    await updateConfig({ reviewNumber: v })
    ;(document.getElementById('modalCfgClose') as HTMLButtonElement | null)?.click()
  }

  return (
    <main className="col-lg-9">
      <div className="panel-surface p-3 mb-3 d-flex align-items-center justify-content-between">
        <h6 className="fw-bold m-0">لغات لایتنر</h6>

        <div className="d-flex align-items-center gap-2 ">
          <div className="dropdown me-2">
            <button className="btn btn-light dropdown-toggle d-flex align-items-center gap-2" data-bs-toggle="dropdown">
              بیشتر <Icon name="list" size={14} />
            </button>
            <ul className="dropdown-menu">
              <li><button className="dropdown-item" data-bs-toggle="modal" data-bs-target="#learnedModal">لغات یادگرفته شده</button></li>
              <li><button className="dropdown-item" onClick={downloadCSV}>دانلود لغات</button></li>
              <li><button className="dropdown-item" data-bs-toggle="modal" data-bs-target="#leitnerAddModal">لغت جدید</button></li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2">
          <button className="btn btn-secondary d-flex align-items-center gap-1" data-bs-toggle="modal" data-bs-target="#leitnerCfgModal" title="تنظیمات">
            <Icon name="gear" size={16} />
          </button>
        </div>
        </div>
        
      </div>

      <div className="panel-surface p-3">
        <div className="text-center mb-3">
          <button className="btn btn-outline-success btn-lg fw-bold px-5 rounded-pill shadow-sm" onClick={() => navigate('/panel/leitner/study')}>
            شروع مطالعه کلمات امروز
          </button>
        </div>
        <hr className='text-secondary-subtle'/>
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 mt-2">
          {boxCards.map((b) => (
            <div className="col" key={b.n}>
              <BoxSummary box={b.n} dueCount={b.count} onStart={() => navigate(`/panel/leitner/review/${b.n}`)} />
            </div>
          ))}
        </div>
      </div>

      {/* Add word modal */}
      <div className="modal modal-slide-up fade" id="leitnerAddModal" tabIndex={-1}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">افزودن لغت جدید</h6>
              <button id="modalAddClose" type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="mb-2">
                <label className="form-label">واژه</label>
                <input className="form-control" value={front} onChange={(e)=>setFront(e.target.value)} placeholder="abate" />
              </div>
              <div className="small panel-muted">کلمات جدید به صورت پیش‌فرض به جعبه ۱ اضافه می‌شوند.</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" data-bs-dismiss="modal">انصراف</button>
              <button className="btn btn-primary" disabled={!front.trim() || creating} onClick={onCreate}>افزودن</button>
            </div>
          </div>
        </div>
      </div>

      {/* Learned list modal */}
      <div className="modal modal-slide-up fade" id="learnedModal" tabIndex={-1}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">لغات یادگرفته شده</h6>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              {!(learned?.length) ? (
                <div className="text-center py-4 panel-muted">موردی یافت نشد</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead><tr><th>واژه</th><th>تاریخ یادگیری</th><th>جعبه</th></tr></thead>
                    <tbody>
                      {learned!.map((it) => (
                        <tr key={it.id}><td>{it.word}</td><td>{formatTehranDate(it.learnedAt)}</td><td>{it.boxName ?? 'Box 5'}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">بستن</button>
              <button type="button" className="btn btn-outline-primary" onClick={downloadCSV}>دانلود CSV</button>
            </div>
          </div>
        </div>
      </div>

      {/* Config modal */}
      <div className="modal modal-slide-up fade" id="leitnerCfgModal" tabIndex={-1}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">تنظیم تعداد مرور روزانه</h6>
              <button id="modalCfgClose" type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <label className="form-label small">حد پیش‌فرض مرور در هر نوبت</label>
              <input type="number" min={1} max={200} className="form-control" value={reviewNumber} onChange={(e)=>setReviewNumber(Number(e.target.value))} />
              <div className="small panel-muted mt-2">پیش‌فرض ۳۰. مقدار بین ۱ تا ۲۰۰.</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" data-bs-dismiss="modal">بستن</button>
              <button className="btn btn-primary" onClick={saveConfig}>ذخیره</button>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

function BoxSummary({ box, dueCount, onStart }: { box: number; dueCount: number; onStart: () => void }) {
  const token = useSelector((s: RootState) => s.auth.token)
  const [total, setTotal] = useState<number | null>(null)

  useEffect(() => {
    let aborted = false
    const load = async () => {
      try {
        let cursor: string | undefined = undefined
        let count = 0
        for (let i = 0; i < 20; i++) { // hard cap 2000
          const qs = new URLSearchParams()
          qs.set('box', String(box))
          qs.set('limit', '100')
          if (cursor) qs.set('cursor', cursor)
          const res = await fetch(`/api/cards?${qs.toString()}`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
          const data = await res.json()
          count += (data?.items?.length ?? 0)
          cursor = data?.nextCursor ?? null
          if (!cursor) break
        }
        if (!aborted) setTotal(count)
      } catch (_) { if (!aborted) setTotal(0) }
    }
    load()
    return () => { aborted = true }
  }, [box, token])

  return (
    <div className="h-100 p-3 rounded-4 border d-flex flex-column justify-content-between">
      <div>
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="text-info"><Icon name="folder" size={22} strokeWidth={1.6} /></span>
          <strong>جعبه {box}</strong>
        </div>
        <div className="small">مرور امروز: <b>{dueCount}</b></div>
        <div className="small">کل کلمات: <b>{total ?? '...'}</b></div>
      </div>
      <div className="mt-3">
        <button className="btn btn-primary w-100" onClick={onStart}>مشاهده کلمات</button>
      </div>
    </div>
  )
}
